<!--
	@component
	A single data row in the accounting mail timeline grid.
	Renders three grid cells: dispatch date / date picker, mail subject, and mail status.
-->
<script lang="ts">
	import { t } from '$i18n/i18n'
	import DatePickerInput from '$lib/atoms/DatePickerInput.svelte'
	import { LotMailNotificationType } from '$lib/types/enums'
	import type { AccountingMailData } from '$types/accountingMailConfigData'
	import { accountingMailConfigState } from './accountingMailConfigState.svelte'

	let {
		index,
		mailData,
		today,
		onMailDurationChange,
		onShowSentMail,
	}: {
		index: number
		mailData: AccountingMailData
		today: Date
		onMailDurationChange: (mailType: LotMailNotificationType, newDuration: number | undefined) => void
		onShowSentMail: (index: number, mailingListStr: string) => void
	} = $props()

	const lotMailTypes: LotMailNotificationType[] = [
		LotMailNotificationType.FIRST_MAIL,
		LotMailNotificationType.SECOND_MAIL,
		LotMailNotificationType.THIRD_MAIL,
		LotMailNotificationType.FOURTH_MAIL,
	]

	const mailType = $derived(lotMailTypes[index] as LotMailNotificationType)

	const datePickerDisabled = $derived(
		accountingMailConfigState.readOnly ||
		accountingMailConfigState.actualTargetComparisonFilled ||
		accountingMailConfigState.isMailDispatchingDisabled
	)

	const datePickerMaxDate = new Date(new Date().getFullYear() + 25, 11, 31)

	const disableDays = (date: Date): boolean => {
		const formattedDate = date.toLocaleDateString('en-CA')
		return (
			date.getDay() === 0 ||
			date.getDay() === 6 ||
			accountingMailConfigState.holidayDaysList.includes(formattedDate) ||
			date < new Date()
		)
	}

	function getLotMailDurationDate(): number | undefined {
		switch (index) {
			case 0: return accountingMailConfigState.firstLotMailDurationDate
			case 1: return accountingMailConfigState.secondLotMailDurationDate
			case 2: return accountingMailConfigState.thirdLotMailDurationDate
			case 3: return accountingMailConfigState.fourthLotMailDurationDate
			default: return undefined
		}
	}

	function setLotMailDurationDate(value: number | undefined): void {
		switch (index) {
			case 0: accountingMailConfigState.firstLotMailDurationDate = value; break
			case 1: accountingMailConfigState.secondLotMailDurationDate = value; break
			case 2: accountingMailConfigState.thirdLotMailDurationDate = value; break
			case 3: accountingMailConfigState.fourthLotMailDurationDate = value; break
		}
	}
</script>

<div class="row-wrapper">
	{#if mailData.dispatchDateMail}
		<div>{mailData.dispatchDateMail}</div>
	{:else if index === 4 || index < accountingMailConfigState.indexNextManualMail || (accountingMailConfigState.actualTargetComparisonFilled && mailData.state !== 'Versendet' && mailData.state !== 'Ausstehend')}
		<div>-</div>
	{:else}
		<div>
			<DatePickerInput
				id="sendDate-{index}"
				label=""
				minDate={today}
				materialWidth="50"
				bind:date={getLotMailDurationDate, setLotMailDurationDate}
				maxDate={datePickerMaxDate}
				placeholder="Versanddatum"
				disabled={datePickerDisabled}
				{disableDays}
				onchange={() => onMailDurationChange(mailType, getLotMailDurationDate())}
			/>
		</div>
	{/if}

	{#if mailData.dispatchDateMail}
		<div class="dispatch-date-mail-sent">
			{mailData.subject}
			<button
				class="hover-pointer link-button"
				onclick={() => onShowSentMail(index, '<br>' + mailData.mailingList.toString().replace(',', '<br>'))}
			>{$t('UI.mail.config.linkTitle')}</button>
		</div>
	{:else if index === accountingMailConfigState.indexNextManualMail && index !== 4 && !accountingMailConfigState.actualTargetComparisonFilled}
		<div class="dispatch-date-mail-next">
			{mailData.subject}
			<span>{$t('UI.mail.config.linkTitle')}</span>
		</div>
	{:else}
		<div class="dispatch-date-mail">
			{mailData.subject}
			<span>{$t('UI.mail.config.linkTitle')}</span>
		</div>
	{/if}

	{#if mailData.dispatchDateMail}
		<div class="sent-mail-identifier">{mailData.state}</div>
	{:else if accountingMailConfigState.isMailDispatchingDisabled}
		<div class="mail-dispatching-paused">
			{accountingMailConfigState.actualTargetComparisonFilled ? 'Wurde nicht versendet' : 'Pausiert'}
		</div>
	{:else}
		<div class="mail-date">
			{#if index === 4}
				<span>Wird automatisch versendet bei Eingabe von</span>
				<span>Ist-Werten</span>
			{:else if parseInt(mailData.state) < 0 || (mailData.state === '0' && new Date().getHours() >= 8) || accountingMailConfigState.indexNextManualMail > index}
				<span class="red-color">Mail übersprungen</span>
			{:else if mailData.state === '0' && new Date().getHours() < 8}
				<span>Wird heute um 8 Uhr versendet</span>
			{:else}
				<span>Wird in</span>
				<span>{mailData.state} Tagen</span>
				<span>versendet</span>
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	@import '../../../styles/global';

	.row-wrapper {
		display: contents;

		& div.mail-dispatching-paused {
			color: $primaryRed !important;
		}

		& > div {
			text-align: start;
			border-bottom: 1px solid $grey-600;
			padding: 0.75rem 0.5rem 0.64rem 0.5rem;
		}

		& > div:first-child {
			padding-left: 2rem;
			display: flex;
			align-items: center;
		}

		& div:last-child {
			@include roboto-font(1.125rem, 500, 0.875rem, $primaryGreen);

			display: flex;
			align-items: center;
		}
	}

	.mail-date {
		display: flex;

		& span {
			@include roboto-font(1.125rem, 400, 0.875rem, black);

			align-self: center;
			display: contents;
		}

		& span:nth-child(2) {
			margin-left: 0.3rem;
			margin-right: 0.3rem;
			align-self: center;
			display: contents;

			@include roboto-font(1.125rem, 500, 0.875rem, $primaryGreen);
		}

		& span.red-color {
			color: $primaryRed;
		}
	}

	.dispatch-date-mail {
		@include roboto-font(1.125rem, 400, 0.875rem, black);

		& span {
			@include roboto-font(1.5rem, 400, 0.875rem, $bluish-grey-700);

			color: $bluish-grey-700;
		}
	}

	.dispatch-date-mail-sent {
		@include roboto-font(1.125rem, 400, 0.875rem, rgba(0, 0, 0, 0.5));

		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.dispatch-date-mail-next {
		@include roboto-font(1.125, 500, 0.875rem, black);

		& span {
			@include roboto-font(1.5rem, 400, 0.875rem, $bluish-grey-700);

			color: $bluish-grey-700;
		}
	}

	.link-button {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		color: var(--color-green-500);
	}
</style>
